import React, { FC, useCallback } from 'react'
import classNames from 'classnames'
import { useMediaQuery } from 'react-responsive'
import { Button, ButtonVariant } from '@/atoms/Button'
import { ButtonSize } from '@/atoms/Button/ButtonUtil/ButtonUtil'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { useDisclose } from '@/hooks/useDisclose'
import { Modal } from '@/molecules/Modal'
import { SocialSharing } from '@/organisms/SocialSharing'
import { useNativeMobileShare } from '@/services/Sharing'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  shareUrl: string
  title?: string
  message?: string
  className?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  textClassName?: string
  shareIconClassName?: string
}

export const ShareButton: FC<Props> = ({
  shareUrl,
  title,
  message,
  className,
  buttonVariant = 'offwhite',
  buttonSize = 'md',
  textClassName,
  shareIconClassName,
}) => {
  const { t } = useTranslate('tickets')
  const { isOpen, onClose, onOpen: onOpenShareModal } = useDisclose(false)
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const { share } = useNativeMobileShare()

  const handleShareClick = useCallback(async () => {
    if (isMobile) {
      await share({
        title,
        text: message,
        url: shareUrl,
        onShareFail: onOpenShareModal,
      })
    } else {
      onOpenShareModal()
    }
  }, [isMobile, message, onOpenShareModal, share, shareUrl, title])

  return (
    <>
      <Button
        size={buttonSize}
        className={classNames('flex items-center gap-1.5 rounded-xl', className)}
        variant={buttonVariant}
        onClick={handleShareClick}
      >
        <ShareOutlineIcon className={shareIconClassName} size={24} color="white" />
        <span className={classNames('hidden lg:inline', textClassName)}>{t('share', 'Share')}</span>
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} transition="slide" panelClasses="!max-w-[480px] ">
        <div className="m-4">
          <div className="flex cursor-pointer flex-row justify-end" onClick={onClose}>
            <CloseIcon size={24} />
          </div>
          <SocialSharing url={shareUrl} vertical={true} />
        </div>
      </Modal>
    </>
  )
}
